/**
 * Notification utility for endpoint requests
 * Handles notifications for admin, backend, and frontend users
 */

export interface EndpointRequestNotification {
  id: string;
  type: "request_created" | "request_completed";
  requestId: string;
  component: string;
  task: string;
  endpoint?: string;
  createdBy?: string;
  approvedBy?: string;
  timestamp: string;
  read: boolean;
}

const NOTIFICATION_STORAGE_KEY = "endpoint_request_notifications";
const ADMIN_NOTIFICATION_COUNT_KEY = "endpoint_requests_admin_notification_count";
const BACKEND_NOTIFICATION_COUNT_KEY = "endpoint_requests_backend_notification_count";
const FRONTEND_NOTIFICATION_COUNT_KEY = "endpoint_requests_frontend_notification_count";

/**
 * Get all notifications for a specific user type
 */
export const getNotifications = (userType: "admin" | "backend" | "frontend"): EndpointRequestNotification[] => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (!stored) return [];

    const allNotifications: EndpointRequestNotification[] = JSON.parse(stored);
    
    // Filter notifications based on user type
    return allNotifications.filter((notif) => {
      if (userType === "admin") {
        // Admin sees all notifications
        return notif.type === "request_created" || notif.type === "request_completed";
      } else if (userType === "backend") {
        // Backend sees only new requests
        return notif.type === "request_created";
      } else {
        // Frontend sees only completed requests for their own requests
        return notif.type === "request_completed";
      }
    });
  } catch (error) {
    console.error("Error getting notifications:", error);
    return [];
  }
};

/**
 * Get unread notification count for a user type
 */
export const getUnreadCount = (userType: "admin" | "backend" | "frontend"): number => {
  const notifications = getNotifications(userType);
  return notifications.filter((n) => !n.read).length;
};

/**
 * Add a new notification
 */
export const addNotification = (
  notification: Omit<EndpointRequestNotification, "id" | "timestamp" | "read">
): void => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    const allNotifications: EndpointRequestNotification[] = stored ? JSON.parse(stored) : [];

    const newNotification: EndpointRequestNotification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false,
    };

    allNotifications.unshift(newNotification); // Add to beginning
    // Keep only last 100 notifications
    const limited = allNotifications.slice(0, 100);

    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(limited));

    // Update notification counts
    updateNotificationCounts();

    // Dispatch custom event
    window.dispatchEvent(new CustomEvent("endpoint-request-notification", {
      detail: newNotification
    }));
  } catch (error) {
    console.error("Error adding notification:", error);
  }
};

/**
 * Mark notification as read
 */
export const markAsRead = (notificationId: string): void => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (!stored) return;

    const allNotifications: EndpointRequestNotification[] = JSON.parse(stored);
    const updated = allNotifications.map((n) =>
      n.id === notificationId ? { ...n, read: true } : n
    );

    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(updated));
    updateNotificationCounts();
  } catch (error) {
    console.error("Error marking notification as read:", error);
  }
};

/**
 * Mark all notifications as read for a user type
 */
export const markAllAsRead = (userType: "admin" | "backend" | "frontend"): void => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (!stored) return;

    const allNotifications: EndpointRequestNotification[] = JSON.parse(stored);
    const updated = allNotifications.map((n) => {
      if (userType === "admin") {
        return { ...n, read: true };
      } else if (userType === "backend" && n.type === "request_created") {
        return { ...n, read: true };
      } else if (userType === "frontend" && n.type === "request_completed") {
        return { ...n, read: true };
      }
      return n;
    });

    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(updated));
    updateNotificationCounts();
  } catch (error) {
    console.error("Error marking all as read:", error);
  }
};

/**
 * Update notification counts in localStorage
 */
const updateNotificationCounts = (): void => {
  const adminCount = getUnreadCount("admin");
  const backendCount = getUnreadCount("backend");
  const frontendCount = getUnreadCount("frontend");

  localStorage.setItem(ADMIN_NOTIFICATION_COUNT_KEY, adminCount.toString());
  localStorage.setItem(BACKEND_NOTIFICATION_COUNT_KEY, backendCount.toString());
  localStorage.setItem(FRONTEND_NOTIFICATION_COUNT_KEY, frontendCount.toString());

  // Dispatch event to update UI
  window.dispatchEvent(new CustomEvent("endpoint-request-notification-count-updated"));
};

/**
 * Get notification count for a user type
 */
export const getNotificationCount = (userType: "admin" | "backend" | "frontend"): number => {
  try {
    const key =
      userType === "admin"
        ? ADMIN_NOTIFICATION_COUNT_KEY
        : userType === "backend"
        ? BACKEND_NOTIFICATION_COUNT_KEY
        : FRONTEND_NOTIFICATION_COUNT_KEY;

    const count = localStorage.getItem(key);
    return count ? parseInt(count, 10) : 0;
  } catch (error) {
    console.error("Failed to read notification count:", error);
    return 0;
  }
};

/**
 * Notify when a new endpoint request is created
 */
export const notifyRequestCreated = (request: {
  id: string;
  component: string;
  task: string;
  createdBy?: string;
}): void => {
  addNotification({
    type: "request_created",
    requestId: request.id,
    component: request.component,
    task: request.task,
    createdBy: request.createdBy,
  });
};

/**
 * Notify when an endpoint request is completed
 */
export const notifyRequestCompleted = (request: {
  id: string;
  component: string;
  task: string;
  endpoint?: string;
  approvedBy?: string;
  createdBy?: string;
}): void => {
  addNotification({
    type: "request_completed",
    requestId: request.id,
    component: request.component,
    task: request.task,
    endpoint: request.endpoint,
    approvedBy: request.approvedBy,
    createdBy: request.createdBy,
  });
};

